import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { EmailLink } from "@/components/EmailLink";
import { openCookieSettings, useConsentChoice } from "@/lib/consent";

interface CookieRow {
  name: string;
  what: string;
  lasts: string;
}

const necessary: CookieRow[] = [
  { name: "cm_session", what: "Keeps you signed in to ClearMakes.", lasts: "Until you sign out, or 7 days" },
  { name: "cm_consent", what: "Remembers whether you said yes or no to analytics, so we don't ask on every page.", lasts: "12 months" },
  {
    name: "Paddle checkout",
    what: "Only on payment pages. Paddle, who handles our payments, uses its own cookies to take the payment safely and stop fraud.",
    lasts: "Set by Paddle",
  },
];

const analytics: CookieRow[] = [
  { name: "_ga, _ga_*", what: "Google Analytics. Counts visits and shows which pages people read.", lasts: "Up to 2 years" },
  { name: "_clck", what: "Microsoft Clarity. Recognises the same browser when it comes back.", lasts: "1 year" },
  { name: "_clsk", what: "Microsoft Clarity. Joins the pages of one visit together.", lasts: "1 day" },
  { name: "CLID", what: "Microsoft Clarity (set on clarity.ms). Recognises the same browser across sites that use Clarity.", lasts: "1 year" },
];

const CookieTable = ({ rows, caption }: { rows: CookieRow[]; caption: string }) => (
  <div className="mt-6 overflow-hidden rounded-xl border border-border">
    <table className="w-full border-collapse text-left text-[15px]">
      <caption className="sr-only">{caption}</caption>
      <thead className="hidden bg-muted/60 sm:table-header-group">
        <tr>
          <th scope="col" className="px-5 py-3 text-[13px] font-medium text-muted-foreground">Name</th>
          <th scope="col" className="px-5 py-3 text-[13px] font-medium text-muted-foreground">What it does</th>
          <th scope="col" className="px-5 py-3 text-[13px] font-medium text-muted-foreground">How long it lasts</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} className="block border-t border-border first:border-t-0 sm:table-row sm:first:border-t">
            <th scope="row" className="block px-5 pt-4 font-mono text-[14px] font-medium text-foreground sm:table-cell sm:py-4 sm:align-top">
              {r.name}
            </th>
            <td className="block px-5 pt-1 leading-relaxed text-muted-foreground sm:table-cell sm:py-4 sm:align-top">{r.what}</td>
            <td className="block px-5 pb-4 pt-1 text-foreground sm:table-cell sm:whitespace-nowrap sm:py-4 sm:align-top">{r.lasts}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const choiceText = {
  granted: "You said yes. Analytics are on.",
  denied: "You said no. Analytics are off.",
  "": "You haven't chosen yet, so analytics are off.",
};

const Cookies = () => {
  const choice = useConsentChoice();
  return (
    <>
      <PageMeta
        title="Cookies | ClearMakes"
        description="Which cookies clearmakes.com uses, why, and how long they last. Analytics run only if you agree, and you can change your choice at any time."
        path="/cookies"
      />
      <main className="bg-background text-foreground">
        <section className="pb-12 pt-16 sm:pb-16 sm:pt-24">
          <div className="cm-container max-w-3xl">
            <p className="cm-eyebrow mb-3">Last updated: September 28, 2026</p>
            <h1 className="cm-h2">Cookies</h1>
            <p className="cm-lead mt-6">
              Cookies are small files a website keeps in your browser. We use the ones we need to keep you signed in. We use
              analytics cookies only if you say yes.
            </p>
          </div>
        </section>

        <section className="border-t border-border py-12 sm:py-16">
          <div className="cm-container max-w-3xl">
            <h2 className="cm-h3 text-2xl">Your choice</h2>
            <p className="cm-body mt-3 text-[17px]" aria-live="polite">
              {choiceText[choice]}
            </p>
            <button type="button" onClick={openCookieSettings} className="cm-btn-secondary mt-6">
              Change my choice
            </button>
          </div>
        </section>

        <section className="border-t border-border py-12 sm:py-16">
          <div className="cm-container max-w-3xl">
            <h2 className="cm-h3 text-2xl">Cookies we always use</h2>
            <p className="cm-body mt-3 text-[17px]">
              The site doesn't work without these, so they don't need your permission. They are not used to follow you.
            </p>
            <CookieTable rows={necessary} caption="Cookies we always use" />
          </div>
        </section>

        <section className="border-t border-border py-12 sm:py-16">
          <div className="cm-container max-w-3xl">
            <h2 className="cm-h3 text-2xl">Analytics, only if you say yes</h2>
            <p className="cm-body mt-3 text-[17px]">
              These show us how people use clearmakes.com and the sign-in page, so we can make them better. Microsoft
              Clarity also records how you move around a page (clicks, scrolling and mouse movement) so we can see where
              people get stuck. It hides what you type. If you say no, Clarity doesn't load at all, and Google Analytics
              sets no cookies.
            </p>
            <CookieTable rows={analytics} caption="Analytics cookies, only with your permission" />
            <p className="cm-body mt-4">
              The signed-in ClearMakes app doesn't load any analytics.
            </p>
          </div>
        </section>

        <section className="border-t border-border py-12 sm:py-16">
          <div className="cm-container max-w-3xl">
            <h2 className="cm-h3 text-2xl">How to change your mind</h2>
            <p className="cm-body mt-3 text-[17px]">
              Press <strong className="font-medium text-foreground">Change my choice</strong> above, or{" "}
              <strong className="font-medium text-foreground">Cookie settings</strong> at the bottom of any page. If you
              say no after saying yes, we remove the analytics cookies we can reach. You can also delete cookies in your
              browser's settings at any time. Your choice is kept for 12 months, then we ask again.
            </p>
            <p className="cm-body mt-4 text-[17px]">
              More about how we handle your data is in our{" "}
              <a href="/privacy" className="cm-link">Privacy Policy</a>. Questions? Email{" "}
              <EmailLink email="contact@clearmakes.com" className="cm-link">contact@clearmakes.com</EmailLink>.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Cookies;
